import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useModules } from "../contexts/ModulesContext";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Geography() {
  const { t } = useI18n();
  const { isEnabled } = useModules();
  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="geography-page">
      <div className="mb-6">
        <h1 className="font-heading text-4xl font-bold tracking-tight text-warm-dark">{t("geography")}</h1>
        <p className="text-sm text-warm-dark/60 mt-1">Référentiels géographiques en cascade</p>
      </div>
      <Tabs defaultValue={isEnabled("pays") ? "pays" : "regions"}>
        <TabsList>
          {isEnabled("pays") && <TabsTrigger value="pays" data-testid="tab-pays">{t("pays")}</TabsTrigger>}
          {isEnabled("regions") && <TabsTrigger value="regions" data-testid="tab-regions">{t("regions")}</TabsTrigger>}
          {isEnabled("sites") && <TabsTrigger value="sites" data-testid="tab-sites">{t("sites")}</TabsTrigger>}
        </TabsList>
        {isEnabled("pays") && <TabsContent value="pays"><PaysTab /></TabsContent>}
        {isEnabled("regions") && <TabsContent value="regions"><RegionsTab /></TabsContent>}
        {isEnabled("sites") && <TabsContent value="sites"><SitesTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function PaysTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["pays"], queryFn: async () => (await api.get("/pays", { params: { limit: 200 } })).data.data });
  const [form, setForm] = useState({ label: "", iso_code: "" });
  const add = async () => {
    if (!form.label || !form.iso_code) return;
    await api.post("/pays", form);
    setForm({ label: "", iso_code: "" });
    qc.invalidateQueries({ queryKey: ["pays"] });
    toast.success("Pays ajouté");
  };
  const del = async (id) => { await api.delete(`/pays/${id}`); qc.invalidateQueries({ queryKey: ["pays"] }); };
  return (
    <Card className="border-sand shadow-soft p-6 mt-4">
      <div className="flex items-end gap-3 mb-5">
        <div className="flex-1"><Label className="text-xs">Libellé</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="pays-label" /></div>
        <div className="w-32"><Label className="text-xs">Code ISO</Label><Input value={form.iso_code} onChange={(e) => setForm({ ...form, iso_code: e.target.value.toUpperCase() })} className="mono" data-testid="pays-iso" /></div>
        <Button onClick={add} className="bg-terracotta hover:bg-terracotta-dark" data-testid="pays-add"><Plus size={14} className="mr-1.5" />Ajouter</Button>
      </div>
      <SimpleTable rows={data || []} columns={[
        { key: "label", label: "Libellé" },
        { key: "iso_code", label: "Code ISO", mono: true },
      ]} onDelete={del} testPrefix="pays" />
    </Card>
  );
}

function RegionsTab() {
  const qc = useQueryClient();
  const { data: pays } = useQuery({ queryKey: ["pays"], queryFn: async () => (await api.get("/pays", { params: { limit: 200 } })).data.data });
  const { data } = useQuery({ queryKey: ["regions"], queryFn: async () => (await api.get("/regions", { params: { limit: 500 } })).data.data });
  const paysMap = useMemo(() => Object.fromEntries((pays || []).map((p) => [p.id, p.label])), [pays]);
  const [form, setForm] = useState({ label: "", pays_id: "" });
  const add = async () => { if (!form.label || !form.pays_id) return; await api.post("/regions", form); setForm({ label: "", pays_id: "" }); qc.invalidateQueries({ queryKey: ["regions"] }); toast.success("Région ajoutée"); };
  const del = async (id) => { await api.delete(`/regions/${id}`); qc.invalidateQueries({ queryKey: ["regions"] }); };
  return (
    <Card className="border-sand shadow-soft p-6 mt-4">
      <div className="flex items-end gap-3 mb-5">
        <div className="flex-1"><Label className="text-xs">Libellé</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="region-label" /></div>
        <div className="w-56"><Label className="text-xs">Pays</Label>
          <Select value={form.pays_id} onValueChange={(v) => setForm({ ...form, pays_id: v })}>
            <SelectTrigger data-testid="region-pays"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
            <SelectContent>{(pays || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={add} className="bg-terracotta hover:bg-terracotta-dark" data-testid="region-add"><Plus size={14} className="mr-1.5" />Ajouter</Button>
      </div>
      <SimpleTable rows={(data || []).map((r) => ({ ...r, _pays: paysMap[r.pays_id] }))} columns={[
        { key: "label", label: "Libellé" },
        { key: "_pays", label: "Pays" },
      ]} onDelete={del} testPrefix="region" />
    </Card>
  );
}

function SitesTab() {
  const qc = useQueryClient();
  const { data: pays } = useQuery({ queryKey: ["pays"], queryFn: async () => (await api.get("/pays", { params: { limit: 200 } })).data.data });
  const { data: regions } = useQuery({ queryKey: ["regions"], queryFn: async () => (await api.get("/regions", { params: { limit: 500 } })).data.data });
  const { data } = useQuery({ queryKey: ["sites"], queryFn: async () => (await api.get("/sites", { params: { limit: 2000 } })).data.data });
  const paysMap = useMemo(() => Object.fromEntries((pays || []).map((p) => [p.id, p.label])), [pays]);
  const regsMap = useMemo(() => Object.fromEntries((regions || []).map((r) => [r.id, r.label])), [regions]);
  const [form, setForm] = useState({ label: "", city: "", address: "", pays_id: "", region_id: "" });
  const filteredRegions = (regions || []).filter((r) => !form.pays_id || r.pays_id === form.pays_id);
  const add = async () => { if (!form.label || !form.pays_id) return; await api.post("/sites", { ...form, region_id: form.region_id || null }); setForm({ label: "", city: "", address: "", pays_id: "", region_id: "" }); qc.invalidateQueries({ queryKey: ["sites"] }); toast.success("Site ajouté"); };
  const del = async (id) => { await api.delete(`/sites/${id}`); qc.invalidateQueries({ queryKey: ["sites"] }); };
  return (
    <Card className="border-sand shadow-soft p-6 mt-4">
      <div className="grid grid-cols-5 gap-3 mb-2">
        <div><Label className="text-xs">Libellé</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="site-label" /></div>
        <div><Label className="text-xs">Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="site-city" /></div>
        <div><Label className="text-xs">Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="site-addr" /></div>
        <div><Label className="text-xs">Pays</Label>
          <Select value={form.pays_id} onValueChange={(v) => setForm({ ...form, pays_id: v, region_id: "" })}>
            <SelectTrigger data-testid="site-pays"><SelectValue placeholder="…" /></SelectTrigger>
            <SelectContent>{(pays || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Région</Label>
          <Select value={form.region_id} onValueChange={(v) => setForm({ ...form, region_id: v })}>
            <SelectTrigger data-testid="site-region"><SelectValue placeholder="…" /></SelectTrigger>
            <SelectContent>{filteredRegions.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end mb-5">
        <Button onClick={add} className="bg-terracotta hover:bg-terracotta-dark" data-testid="site-add"><Plus size={14} className="mr-1.5" />Ajouter</Button>
      </div>
      <SimpleTable rows={(data || []).map((s) => ({ ...s, _pays: paysMap[s.pays_id], _region: regsMap[s.region_id] }))} columns={[
        { key: "label", label: "Libellé" },
        { key: "city", label: "Ville" },
        { key: "_region", label: "Région" },
        { key: "_pays", label: "Pays" },
      ]} onDelete={del} testPrefix="site" />
    </Card>
  );
}

function SimpleTable({ rows, columns, onDelete, testPrefix }) {
  return (
    <div className="overflow-x-auto border border-sand rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-cream">
          <tr>
            {columns.map((c) => <th key={c.key} className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wider text-warm-dark/60">{c.label}</th>)}
            <th className="w-12"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sand">
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length + 1} className="text-center py-8 text-warm-dark/50 text-sm">Aucune donnée</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} className="hover:bg-cream" data-testid={`${testPrefix}-row-${r.id}`}>
              {columns.map((c) => <td key={c.key} className={`px-4 py-2 text-warm-dark/90 ${c.mono ? "mono text-xs" : ""}`}>{r[c.key] || "—"}</td>)}
              <td className="px-2 text-right">
                <button onClick={() => onDelete(r.id)} className="text-warm-dark/50 hover:text-red-600" data-testid={`${testPrefix}-del-${r.id}`}><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
